/**
 * PATH: packages/email-hub/src/feature/components/RichTextEditor.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-RICHTEXTEDITOR-002
 * PURPOSE: Canonical email body editor — now built on TipTap/ProseMirror
 *          (was a custom contentEditable + deprecated execCommand). Keeps the
 *          SAME public API (RichTextEditorProps + RichTextEditorHandle) so
 *          SignatureEditorModal, the AI "insert at caret", and the
 *          reply/forward/signature paths in useComposePage are unchanged.
 * WHY:     execCommand caused the broken list/Enter behaviour and blocked
 *          tables, slash-commands and embedded Kanban cards. ProseMirror is a
 *          real document model: lists continue on Enter, markdown input rules
 *          work, and rich tables + formulas + card chips become possible.
 * FLOW:
 *   value ─▶ useEditor(content) ─▶ EditorContent
 *   typing ─▶ onUpdate ─▶ onChange(getHTML)  (native HTML; baked to email-safe on send)
 *   "/" ─▶ slash menu ; "@" ─▶ mention ; right files ─▶ inline image / onFile
 * RELATED:
 *   - components/rich-text/*: extensions, toolbar, slash, mention, tables,
 *     formulas, card node, email serialiser.
 *   - apps/<tenant>/.../RichTextEditor.tsx: thin re-export shim.
 */

import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import type { Editor } from '@tiptap/core'
import {
  compressImageToDataUrl, extractImagesFromPaste, prepareInlineImage,
  htmlHasImage, inlinePastedHtmlImages,
} from '@quotech/email-hub/feature/lib/imageClipboard'
import { fetchImageViaProxy } from '@quotech/email-hub/feature/lib/imageProxy'
import { sanitizeEmailHtml } from './rich-text/emailHtml'
import { forceWrapSignatureBlock } from './rich-text/signatureBlock'
import { useToast } from '../../_internal/components/Toast'
import { searchEmailContacts } from './RecipientAutocomplete'
import { buildExtensions, QTE_EDITOR_CSS } from './rich-text/extensions'
import { SLASH_ITEMS, type SlashAction } from './rich-text/slash'
import { buildSnippetSlashItems } from './rich-text/snippetSlash'
import { getSelectionOrBodyHtml, getSelectionHtml } from './rich-text/selectionHtml'
import { promptDialog } from '@quotech/ui'
import {
  createSnippet, loadSnippets, removeSnippet, saveSnippets, snippetStorageKey,
  upsertSnippet, type ComposeSnippet,
} from './rich-text/snippetStore'
import { SnippetManagerModal } from './rich-text/SnippetManagerModal'
import { createSuggestionController, SuggestionPopup, useSuggestionState, type SuggestionController } from './rich-text/suggestionController'
import { Toolbar } from './rich-text/Toolbar'
import { CardPicker } from './rich-text/CardPicker'
import { QDriveImagePicker } from './rich-text/QDriveImagePicker'
import { EditorContextMenu, type CtxMenuPos } from './rich-text/EditorContextMenu'
import type { AiSelectionAction } from '@quotech/email-hub/feature/lib/aiCompose'
import { promptInsertLink } from './rich-text/linkPrompt'
import type { KanbanCardAttrs } from './rich-text/kanbanCardNode'
import type { MentionPerson } from './rich-text/mention'

export type { RichTextEditorHandle } from './rich-text/types'
import type { RichTextEditorHandle } from './rich-text/types'

// Re-exported so useComposePage + the placeholder test keep importing it here.
export { typingAreaIsBlank } from './rich-text/emailHtml'

export interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  /** Non-image dropped files → caller's attachment list. Images embed inline. */
  onFile?: (files: File[]) => void
  /** Operator picked a person from the in-body @mention popup → add to recipients. */
  onMention?: (person: { name: string; email: string }) => void
  placeholder?: string
  className?: string
  ariaLabel?: string
  /** Selection-scoped AI polish. Given an action + the selected plain text,
   *  resolve to the revised text (or null to leave the selection untouched).
   *  When omitted, the editor's right-click menu hides the AI actions. */
  onAiTransform?: (action: AiSelectionAction, text: string) => Promise<string | null>
  /** Free-form "Ask AI…": given the operator's instruction and the current
   *  selection HTML (empty when nothing is highlighted), resolve to an HTML
   *  fragment to insert (or null for a no-op). Powers "fill out this table" /
   *  "write a formatted email with these dates". When omitted, the Ask-AI
   *  affordances (toolbar button, slash item, context-menu entry) are hidden. */
  onAiInstruct?: (instruction: string, selectionHtml: string) => Promise<string | null>
  /** Signature-editor mode: a pasted layout TABLE (an Outlook/Apple-Mail
   *  signature) is kept as an OPAQUE block instead of being parsed by the
   *  table schema (which shatters the banner + drops the logo). The block
   *  renders a faithful preview; emailHtml.toEmailHtml expands it on send. */
  signatureMode?: boolean
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    { value, onChange, onFile, onMention, placeholder, className, ariaLabel, onAiTransform, onAiInstruct, signatureMode },
    ref,
  ) {
    const onChangeRef = useRef(onChange); onChangeRef.current = onChange
    const signatureModeRef = useRef(signatureMode); signatureModeRef.current = signatureMode
    const onMentionRef = useRef(onMention); onMentionRef.current = onMention
    const onFileRef = useRef(onFile); onFileRef.current = onFile
    const onAiTransformRef = useRef(onAiTransform); onAiTransformRef.current = onAiTransform
    const onAiInstructRef = useRef(onAiInstruct); onAiInstructRef.current = onAiInstruct
    // useToast() returns a no-op when no ToastProvider is mounted (e.g. the
    // signature modal), so this is safe in every context the editor renders
    // in. Held in a ref so embedFiles stays identity-stable for editorProps.
    const { toast } = useToast()
    const toastRef = useRef(toast); toastRef.current = toast
    const editorInstanceRef = useRef<Editor | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const openImageRef = useRef<() => void>(() => {})
    const openCardRef = useRef<() => void>(() => {})
    const openQDriveRef = useRef<() => void>(() => {})
    const openSnippetSaveRef = useRef<() => void>(() => {})
    const openSnippetManageRef = useRef<() => void>(() => {})
    const openAiRef = useRef<() => void>(() => {})
    const [cardPickerOpen, setCardPickerOpen] = useState(false)
    const [qdrivePickerOpen, setQDrivePickerOpen] = useState(false)
    const [ctxMenu, setCtxMenu] = useState<CtxMenuPos | null>(null)

    // Reusable compose snippets ("templates"). Loaded once from localStorage;
    // a ref mirrors state so the "/" menu's live getter sees the latest list
    // without rebuilding the editor extension list.
    const snippetKey = snippetStorageKey()
    const [snippets, setSnippets] = useState<ComposeSnippet[]>([])
    const snippetsRef = useRef<ComposeSnippet[]>([])
    snippetsRef.current = snippets
    const [snippetModalOpen, setSnippetModalOpen] = useState(false)
    const [pendingSnippetHtml, setPendingSnippetHtml] = useState('')
    useEffect(() => { setSnippets(loadSnippets(snippetKey)) }, [snippetKey])

    const controllerRef = useRef<SuggestionController | null>(null)
    if (!controllerRef.current) controllerRef.current = createSuggestionController()
    const controller = controllerRef.current

    // Stable callbacks so the extension list is built ONCE (a changing
    // extension list would tear down + rebuild the whole editor).
    const stableMention = useRef((p: MentionPerson) => onMentionRef.current?.(p)).current
    const searchContacts = useRef(async (q: string, signal: AbortSignal): Promise<MentionPerson[]> => {
      const items = await searchEmailContacts(q, signal)
      return items.map((s) => ({ name: s.name, email: s.email }))
    }).current
    const stableSlashAction = useRef((action: SlashAction) => {
      if (action === 'card') openCardRef.current()
      else if (action === 'qdrive') openQDriveRef.current()
      else if (action === 'snippet-save') openSnippetSaveRef.current()
      else if (action === 'snippet-manage') openSnippetManageRef.current()
      else if (action === 'ai') openAiRef.current()
      else openImageRef.current()
    }).current
    // Live "/" item list = static blocks + saved snippets + save/manage entries.
    const getSlashItems = useRef(() => [
      ...SLASH_ITEMS,
      ...buildSnippetSlashItems(snippetsRef.current),
    ]).current

    const extensionsRef = useRef<ReturnType<typeof buildExtensions> | null>(null)
    if (!extensionsRef.current) {
      extensionsRef.current = buildExtensions({
        controller,
        placeholder: placeholder || 'Write your message…  (type "/" for blocks, "@" to mention)',
        searchContacts,
        onMention: stableMention,
        onSlashAction: stableSlashAction,
        getSlashItems,
      })
    }

    const embedFiles = useCallback(async (images: File[]) => {
      const ed = editorInstanceRef.current
      if (!ed || images.length === 0) return
      for (const f of images) {
        // prepareInlineImage rasterises SVGs and routes anything too large to
        // embed inline (big GIF / huge screenshot) to the attachment list,
        // so the recipient never gets a silently-broken inline image.
        const prepared = await prepareInlineImage(f)
        if (prepared.kind === 'attach') {
          onFileRef.current?.([prepared.file])
          toastRef.current(prepared.reason, 'info', { persist: false })
          continue
        }
        ed.chain().focus().setImage({ src: prepared.dataUrl, alt: (f.name || 'image').replace(/"/g, '') }).run()
      }
    }, [])

    const editor = useEditor({
      extensions: extensionsRef.current,
      content: value || '',
      editorProps: {
        attributes: {
          class: 'qte-prose',
          role: 'textbox',
          'aria-label': ariaLabel || 'Email body',
          'aria-multiline': 'true',
        },
        handlePaste: (_view, event) => {
          const ev = event as ClipboardEvent
          const html = ev.clipboardData?.getData('text/html') || ''
          const fileImgs = extractImagesFromPaste(ev)
          // SIGNATURE MODE: a pasted layout TABLE (an Outlook/Apple-Mail
          // signature) must NOT go through the table schema — it would be
          // normalised into a rectangular grid (banner shattered, logo
          // dropped). Inline its images so the logo travels, sanitise it, then
          // keep it as ONE opaque block that renders a faithful preview and
          // expands verbatim on send (BUG-2026-06-29-…-LAYOUT-MANGLED).
          if (signatureModeRef.current && html && /<table[\s>]/i.test(html)) {
            ev.preventDefault()
            void (async () => {
              const rescueDataUrls = await Promise.all(
                fileImgs.map((f) => compressImageToDataUrl(f)),
              )
              const inlined = await inlinePastedHtmlImages(html, {
                rescueDataUrls,
                // Corporate signature logos usually live on CORS-less /
                // auth-gated hosts a direct browser fetch can't read —
                // fall back to the backend image proxy so the logo
                // INLINES instead of riding as a URL that renders broken
                // for the recipient (BUG-2026-07-06-SIGNATURE-REMOTE-
                // LOGO-NOT-INLINED).
                fetchBlobViaProxy: fetchImageViaProxy,
              })
              const block = forceWrapSignatureBlock(sanitizeEmailHtml(inlined))
              editorInstanceRef.current?.chain().focus().insertContent(block).run()
            })()
            return true
          }
          // Rich paste that REFERENCES images — a signature/snippet copied
          // from a mail client or web page. Default paste renders cid:/
          // expired srcs as a broken-image icon and never inlines them on
          // send, so intercept and inline-fix every <img>. When the clipboard
          // ALSO carried the bytes (fileImgs), they rescue dead cid:/blob: src.
          if (html && htmlHasImage(html)) {
            ev.preventDefault()
            void (async () => {
              const rescueDataUrls = await Promise.all(
                fileImgs.map((f) => compressImageToDataUrl(f)),
              )
              const fixed = await inlinePastedHtmlImages(html, {
                rescueDataUrls,
                fetchBlobViaProxy: fetchImageViaProxy,
              })
              editorInstanceRef.current?.chain().focus().insertContent(fixed).run()
            })()
            return true
          }
          // Bare image paste (screenshot / "copy image", no HTML) → embed inline.
          if (fileImgs.length) {
            ev.preventDefault()
            void embedFiles(fileImgs)
            return true
          }
          return false
        },
        handleDrop: (_view, event) => {
          const dt = (event as DragEvent).dataTransfer
          const files = Array.from(dt?.files || [])
          if (files.length === 0) return false
          event.preventDefault()
          const imgs = files.filter((f) => f.type.startsWith('image/'))
          const others = files.filter((f) => !f.type.startsWith('image/'))
          if (others.length) onFileRef.current?.(others)
          if (imgs.length) void embedFiles(imgs)
          return true
        },
      },
      onUpdate: ({ editor: ed }) => onChangeRef.current(ed.getHTML()),
    })

    useEffect(() => { editorInstanceRef.current = editor }, [editor])

    const openImagePicker = useCallback(() => fileInputRef.current?.click(), [])
    openImageRef.current = openImagePicker
    openCardRef.current = () => setCardPickerOpen(true)
    openQDriveRef.current = () => setQDrivePickerOpen(true)
    openSnippetSaveRef.current = () => {
      // Capture the selection (or whole body) BEFORE the menu's deleteRange
      // mutates the doc — slash.command already ran deleteRange, so re-read the
      // live editor here for the freshest content.
      setPendingSnippetHtml(getSelectionOrBodyHtml(editorInstanceRef.current))
      setSnippetModalOpen(true)
    }
    openSnippetManageRef.current = () => {
      setPendingSnippetHtml('')
      setSnippetModalOpen(true)
    }

    const handleSaveSnippet = useCallback((title: string) => {
      const snippet = createSnippet({ title, html: pendingSnippetHtml })
      if (!snippet) {
        toastRef.current('Add a title and some content to save a snippet.', 'info', { persist: false })
        return
      }
      setSnippets((prev) => {
        const next = upsertSnippet(prev, snippet)
        saveSnippets(snippetKey, next)
        return next
      })
      setPendingSnippetHtml('')
      toastRef.current(`Saved snippet “${snippet.title}”.`, 'success', { persist: false })
    }, [pendingSnippetHtml, snippetKey])

    const handleDeleteSnippet = useCallback((id: string) => {
      setSnippets((prev) => {
        const next = removeSnippet(prev, id)
        saveSnippets(snippetKey, next)
        return next
      })
    }, [snippetKey])

    const handleInsertSnippet = useCallback((snippet: ComposeSnippet) => {
      editorInstanceRef.current?.chain().focus().insertContent(snippet.html).run()
      setSnippetModalOpen(false)
    }, [])

    // Selection-scoped AI polish (right-click → Rewrite / Shorten / …).
    // Capture the highlighted range BEFORE the await so the menu closing /
    // focus moving can't drift it, then swap exactly that range for the AI's
    // revised text. Plain-text in, rich-text out (insertContentAt parses it).
    const runAiSelection = useCallback(async (action: AiSelectionAction) => {
      const ed = editorInstanceRef.current
      const transform = onAiTransformRef.current
      if (!ed || !transform) return
      const { from, to, empty } = ed.state.selection
      if (empty) {
        toastRef.current('Select some text first to use AI on it.', 'info', { persist: false })
        return
      }
      const text = ed.state.doc.textBetween(from, to, '\n', '\n').trim()
      if (!text) {
        toastRef.current('Select some text first to use AI on it.', 'info', { persist: false })
        return
      }
      toastRef.current(`${action.label}…`, 'info', { persist: false })
      try {
        const revised = await transform(action, text)
        if (!revised) {
          toastRef.current('AI had nothing to change there.', 'info', { persist: false })
          return
        }
        const target = editorInstanceRef.current
        if (!target) return
        target.chain().focus().insertContentAt({ from, to }, revised).run()
      } catch (err) {
        toastRef.current(
          `AI request failed: ${err instanceof Error ? err.message : 'unknown error'}`,
          'error',
        )
      }
    }, [])

    // Free-form "Ask AI…": prompt for an instruction, hand the model the current
    // selection HTML (so "fill out this table" has the table to work on), then
    // drop the returned HTML in, replacing the selection, or at the caret when
    // nothing was highlighted (e.g. "write a formatted email with these dates").
    // The selection range + HTML are captured BEFORE the modal opens because the
    // dialog moves DOM focus; ProseMirror keeps the positions valid for reinsert.
    const runAiInstruct = useCallback(async () => {
      const ed = editorInstanceRef.current
      const instruct = onAiInstructRef.current
      if (!ed || !instruct) return
      const { from, to } = ed.state.selection
      const selectionHtml = getSelectionHtml(ed)
      const instruction = await promptDialog({
        title: 'Ask AI',
        description: selectionHtml
          ? 'What should AI do with the selected text? e.g. “fill out this table with last month’s figures”, “make this a tidy bullet list”.'
          : 'What should AI write? e.g. “a formatted email confirming the site visit with the dates and times below”, “a 3-column table: task, owner, due date”.',
        inputLabel: 'Instruction',
        placeholder: 'Describe what you want…',
        required: true,
        multiline: true,
        submitLabel: 'Generate',
      })
      if (!instruction) return
      toastRef.current('Asking AI…', 'info', { persist: false })
      try {
        const html = await instruct(instruction, selectionHtml)
        if (!html) {
          toastRef.current('AI returned nothing usable.', 'info', { persist: false })
          return
        }
        const target = editorInstanceRef.current
        if (!target) return
        target.chain().focus().insertContentAt({ from, to }, html).run()
      } catch (err) {
        toastRef.current(
          `AI request failed: ${err instanceof Error ? err.message : 'unknown error'}`,
          'error',
        )
      }
    }, [])
    openAiRef.current = runAiInstruct

    const insertQDriveImage = useCallback((src: string, alt: string) => {
      editorInstanceRef.current?.chain().focus().setImage({ src, alt: (alt || 'image').replace(/"/g, '') }).run()
    }, [])

    const onImagesPicked = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'))
      e.target.value = ''
      if (picked.length) void embedFiles(picked)
    }, [embedFiles])

    useImperativeHandle(ref, () => ({
      insertHtml(html: string) {
        const ed = editorInstanceRef.current
        if (!ed) return
        ed.chain().focus().insertContent(html).run()
      },
      setHtml(html: string) {
        const ed = editorInstanceRef.current
        if (!ed) return
        ed.commands.setContent(html || '', { emitUpdate: false })
        onChangeRef.current(ed.getHTML())
      },
      focus() { editorInstanceRef.current?.commands.focus() },
      focusStart() { editorInstanceRef.current?.commands.focus('start') },
    }), [])

    const suggestState = useSuggestionState(controller)

    return (
      <div className={`qte-editor flex flex-col ${className || ''}`}>
        <style>{QTE_EDITOR_CSS}</style>
        <Toolbar
          editor={editor}
          onInsertImage={openImagePicker}
          onAskAi={onAiInstruct ? () => void runAiInstruct() : undefined}
        />
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onImagesPicked} />
        <div
          className="relative flex-1 min-h-0 overflow-y-auto rounded-md border border-border bg-background p-3 text-foreground focus-within:ring-2 focus-within:ring-primary/30"
          onContextMenu={(e) => {
            e.preventDefault()
            const ed = editorInstanceRef.current
            const selectedText = ed && !ed.state.selection.empty
              ? ed.state.doc.textBetween(ed.state.selection.from, ed.state.selection.to, '\n')
              : ''
            setCtxMenu({ x: e.clientX, y: e.clientY, selectedText })
          }}
        >
          <EditorContent editor={editor} />
        </div>
        <SuggestionPopup
          state={suggestState}
          onPick={(i) => controller.select(i)}
          onHover={(i) => controller.setIndex(i)}
        />
        <CardPicker
          open={cardPickerOpen}
          onClose={() => setCardPickerOpen(false)}
          onPick={(attrs: KanbanCardAttrs) => editorInstanceRef.current?.chain().focus().insertKanbanCard(attrs).run()}
        />
        <QDriveImagePicker
          open={qdrivePickerOpen}
          onClose={() => setQDrivePickerOpen(false)}
          onPick={insertQDriveImage}
        />
        <SnippetManagerModal
          open={snippetModalOpen}
          pendingHtml={pendingSnippetHtml}
          snippets={snippets}
          onClose={() => setSnippetModalOpen(false)}
          onSave={handleSaveSnippet}
          onDelete={handleDeleteSnippet}
          onInsert={handleInsertSnippet}
        />
        <EditorContextMenu
          editor={editor}
          pos={ctxMenu}
          onClose={() => setCtxMenu(null)}
          onInsertImage={openImagePicker}
          onInsertQDriveImage={() => setQDrivePickerOpen(true)}
          onInsertCard={() => setCardPickerOpen(true)}
          onInsertLink={() => void promptInsertLink(editorInstanceRef.current)}
          onAiSelection={onAiTransform ? (action) => void runAiSelection(action) : undefined}
          onAskAi={onAiInstruct ? () => void runAiInstruct() : undefined}
        />
      </div>
    )
  },
)

export default RichTextEditor
